import React, { Component } from "react";
import styled from "styled-components";
import { SubscriptionContext } from "../SubscriptionContext";
import {
    Button,
    PortalCaption,
    PortalMetricCard,
    PortalMetricLabel,
    PortalPaymentSummaryAmount,
    PortalPaymentSummaryCard,
    PortalPaymentSummaryLabel,
    PortalPaymentSummaryRow,
    PortalPanelHeader,
    PortalSectionTitle,
    portalColors
} from "src/components";
import { paymentSlashSuffix } from "src/utils/dates";
import { formatCurrency } from "src/utils/currency";

const PriceStack = styled.div`
    width: 100%;
    display: grid;
    gap: 1rem;
`;

const SummaryCard = styled(PortalMetricCard)`
    min-height: auto;
    align-content: start;
    gap: 0.4rem;
`;

const StyledButton = styled(Button)`
    width: 8.75rem;
    height: 2.625rem;
    border-radius: var(--calendar-radius-control, 8px) !important;
    line-height: 100%;
    font-weight: 700;
    font-size: 0.875rem;
    margin-top: 1rem;
    box-sizing: border-box;
`;

class Price extends Component {
    static contextType = SubscriptionContext;

    back = () => {
        this.props.goToOverview();
    };

    render() {
        const {
            totalNumber,
            currentSubscription,
            periodsForPayment,
            currentForm,
            Forms,
            processingFee,
            gst,
            totalAmount
        } = this.props;

        const subscriptionName = currentSubscription?.name || "";
        const frequencyName = currentSubscription?.frequencyName || "";
        const pricePerUserPerPeriodCents = currentSubscription?.periodNetAmountCents || 0;
        const subscriptionPaymentPeriod = currentSubscription?.paymentPeriod || null;
        const subscriptionPeriod = currentSubscription?.durationNumber || 0;
        const subscriptionUnit = currentSubscription?.durationUnit || "";
        const currencyCode = currentSubscription?.currencyCode || "";

        const payingForUnusualNumber = periodsForPayment !== subscriptionPeriod && periodsForPayment !== subscriptionPaymentPeriod;

        const perPeriodSlashSuffix = paymentSlashSuffix(1, subscriptionUnit);
        const perFullDurationSlashSuffix = paymentSlashSuffix(subscriptionPaymentPeriod === null ? subscriptionPeriod : 1, subscriptionUnit);

        const pricePerUserPerPeriod = pricePerUserPerPeriodCents / 100;
        const subTotal = (pricePerUserPerPeriodCents * totalNumber * periodsForPayment) / 100;

        const gstAmount = gst ?? 0;
        const processingFeeAmount = processingFee ?? 0;
        const totalAmountDisplay = totalAmount ?? (subTotal + gstAmount + processingFeeAmount);

        // Check we're on an expected form page
        const isInitialSubscription = currentForm === Forms.PLAN || currentForm === Forms.REVIEW;
        const isAddingUsers = currentForm === Forms.PURCHASE || currentForm === Forms.CHECKOUT;
        const isRenewal = currentForm === Forms.RENEWAL;
        if (!isInitialSubscription && !isAddingUsers && !isRenewal) {
            console.log("Unknown form for Price: " + currentForm);
            return <></>;
        }

        return (
            <PriceStack>
                <PortalPanelHeader withDivider>
                    <div>
                        <PortalSectionTitle>Order Summary</PortalSectionTitle>
                        <PortalCaption margin="0.25rem 0 0">
                            Confirm the subscription cost for this payment.
                        </PortalCaption>
                    </div>
                </PortalPanelHeader>

                <SummaryCard minHeight="auto">
                    <PortalMetricLabel>Subscription Type</PortalMetricLabel>
                    <PortalSectionTitle size="1rem">{subscriptionName}</PortalSectionTitle>
                    <PortalCaption margin="0">{frequencyName}</PortalCaption>
                </SummaryCard>

                <SummaryCard minHeight="auto">
                    <PortalMetricLabel>Subscription Duration</PortalMetricLabel>
                    <PortalSectionTitle size="1rem">{subscriptionName}</PortalSectionTitle>
                </SummaryCard>

                { payingForUnusualNumber &&
                    <SummaryCard minHeight="auto">
                        <PortalMetricLabel>Payments Due</PortalMetricLabel>
                        <PortalSectionTitle size="1rem">{periodsForPayment}</PortalSectionTitle>
                    </SummaryCard>
                }

                <SummaryCard minHeight="auto">
                    <PortalMetricLabel>Number of User Licenses</PortalMetricLabel>
                    <PortalSectionTitle size="1rem">{totalNumber}</PortalSectionTitle>
                </SummaryCard>

                <SummaryCard minHeight="auto">
                    <PortalMetricLabel>Price Per User License</PortalMetricLabel>
                    <PortalSectionTitle size="1rem">
                        {formatCurrency(pricePerUserPerPeriod, currencyCode)}/per {perPeriodSlashSuffix}
                    </PortalSectionTitle>
                </SummaryCard>

                <PortalPaymentSummaryCard>
                    <PortalPaymentSummaryRow>
                        <PortalPaymentSummaryLabel>Subtotal</PortalPaymentSummaryLabel>
                        <PortalPaymentSummaryAmount>
                            { formatCurrency(subTotal, currencyCode) }/{perFullDurationSlashSuffix}
                        </PortalPaymentSummaryAmount>
                    </PortalPaymentSummaryRow>
                    { processingFeeAmount > 0 &&
                        <PortalPaymentSummaryRow>
                            <PortalPaymentSummaryLabel>Processing Fee</PortalPaymentSummaryLabel>
                            <PortalPaymentSummaryAmount weight={700} size="0.875rem">
                                { formatCurrency(processingFeeAmount, currencyCode) }/{perFullDurationSlashSuffix}
                            </PortalPaymentSummaryAmount>
                        </PortalPaymentSummaryRow>
                    }
                    { gstAmount > 0 &&
                        <PortalPaymentSummaryRow>
                            <PortalPaymentSummaryLabel>GST</PortalPaymentSummaryLabel>
                            <PortalPaymentSummaryAmount weight={700} size="0.875rem">
                                { formatCurrency(gstAmount, currencyCode) }/{perFullDurationSlashSuffix}
                            </PortalPaymentSummaryAmount>
                        </PortalPaymentSummaryRow>
                    }
                    <PortalPaymentSummaryRow>
                        <PortalPaymentSummaryLabel>Total</PortalPaymentSummaryLabel>
                        <PortalPaymentSummaryAmount size="1.25rem">
                            { formatCurrency(totalAmountDisplay, currencyCode, true) }/{perFullDurationSlashSuffix}
                        </PortalPaymentSummaryAmount>
                    </PortalPaymentSummaryRow>
                </PortalPaymentSummaryCard>

                {!this.props.hideBackButton && (
                    <StyledButton
                        color={portalColors.text}
                        backgroundColor={portalColors.surface}
                        border={`1px solid ${portalColors.border}`}
                        onClick={() => this.back()}
                    >
                        Back
                    </StyledButton>
                )}
            </PriceStack>
        );
    }
}

export default Price;
